import { useState } from 'react';

/**
 * Utilidades para formateo y validación de RUT chileno
 */

/**
 * Formatea un RUT chileno agregando puntos y guión
 * @param {string} rut - RUT sin formato (ej: "123456789")
 * @returns {string} - RUT formateado (ej: "12.345.678-9")
 */
export const formatearRut = (rut) => {
    if (!rut) return '';

    // Limpiar el RUT de cualquier formato previo
    const rutLimpio = rut.toString().replace(/[^\dkK]/g, '');

    if (rutLimpio.length < 2) return rutLimpio;

    // Separar cuerpo y dígito verificador
    const cuerpo = rutLimpio.slice(0, -1);
    const dv = rutLimpio.slice(-1).toLowerCase();

    // Formatear el cuerpo con puntos
    let cuerpoFormateado = '';
    let contador = 0;

    for (let i = cuerpo.length - 1; i >= 0; i--) {
        if (contador > 0 && contador % 3 === 0) {
            cuerpoFormateado = '.' + cuerpoFormateado;
        }
        cuerpoFormateado = cuerpo[i] + cuerpoFormateado;
        contador++;
    }

    return `${cuerpoFormateado}-${dv}`;
};

/**
 * Limpia un RUT de formato dejando solo números y K
 * @param {string} rut - RUT formateado
 * @returns {string} - RUT sin formato
 */
export const limpiarRut = (rut) => {
    if (!rut) return '';
    return rut.toString().replace(/[^\dkK]/g, '');
};

/**
 * Calcula el dígito verificador de un RUT
 * @param {string|number} rutSinDV - RUT sin dígito verificador (ej: "12345678" o 12345678)
 * @returns {string} - Dígito verificador calculado ('0', '1'-'9', o 'K')
 */
export const calcularDigitoVerificador = (rutSinDV) => {
    if (!rutSinDV) return '';

    const rut = rutSinDV.toString().replace(/\D/g, ''); // Solo números

    if (rut.length < 7 || rut.length > 8) return '';

    let suma = 0;
    let multiplicador = 2;

    // Recorrer de derecha a izquierda
    for (let i = rut.length - 1; i >= 0; i--) {
        suma += parseInt(rut[i]) * multiplicador;
        multiplicador++;
        if (multiplicador > 7) multiplicador = 2;
    }

    const resto = suma % 11;
    const dv = resto === 0 ? '0' : resto === 1 ? 'K' : (11 - resto).toString();

    return dv;
};

/**
 * Formatea un RUT completo calculando automáticamente el dígito verificador
 * @param {string|number} rutSinDV - RUT sin dígito verificador
 * @returns {string} - RUT formateado con dígito verificador (ej: "12.345.678-9")
 */
export const formatearRutCompleto = (rutSinDV) => {
    if (!rutSinDV) return '';

    const rut = rutSinDV.toString().replace(/\D/g, '');

    if (rut.length < 7 || rut.length > 8) return rut;

    const dv = calcularDigitoVerificador(rut);
    const rutConDV = rut + dv;

    return formatearRut(rutConDV);
};

/**
 * Valida si un RUT chileno es válido
 * @param {string} rut - RUT a validar
 * @returns {boolean} - true si es válido
 */
export const validarRut = (rut) => {
    if (!rut) return false;

    const rutLimpio = limpiarRut(rut);

    if (rutLimpio.length < 8 || rutLimpio.length > 9) return false;

    const cuerpo = rutLimpio.slice(0, -1);
    const dv = rutLimpio.slice(-1).toLowerCase();

    // Calcular dígito verificador
    let suma = 0;
    let multiplicador = 2;

    for (let i = cuerpo.length - 1; i >= 0; i--) {
        suma += parseInt(cuerpo[i]) * multiplicador;
        multiplicador++;
        if (multiplicador > 7) multiplicador = 2;
    }

    const resto = suma % 11;
    const dvCalculado = resto === 0 ? '0' : resto === 1 ? 'k' : (11 - resto).toString();

    return dv === dvCalculado;
};

/**
 * Formatea RUT en tiempo real mientras el usuario escribe
 * @param {string} value - Valor actual del input
 * @returns {string} - Valor formateado
 */
export const formatearRutEnTiempoReal = (value) => {
    if (!value) return '';

    // Limpiar y limitar a 9 caracteres
    const rutLimpio = limpiarRut(value).slice(0, 9);

    if (rutLimpio.length <= 1) return rutLimpio;

    return formatearRut(rutLimpio);
};

/**
 * Hook personalizado para manejar input de RUT
 * @param {string} initialValue - Valor inicial
 * @returns {object} - { rut, setRut, rutFormateado, esValido }
 */
export const useRutInput = (initialValue = '') => {
    const [rut, setRutInternal] = useState(limpiarRut(initialValue));

    const setRut = (value) => {
        const rutLimpio = limpiarRut(value).slice(0, 9);
        setRutInternal(rutLimpio);
    };

    const rutFormateado = formatearRut(rut);
    const esValido = validarRut(rut);

    return { rut, setRut, rutFormateado, esValido };
};
